import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Workflow, Play, Pause, Plus, Trash2, Settings, Save, Loader2, RefreshCw,
  Mail, Clock, Users, Filter, GitBranch, Zap, Target, ArrowRight, GripVertical,
  CheckCircle2, XCircle, MoreVertical, Copy, Eye, Edit2, ChevronRight, Sparkles,
  UserPlus, ShoppingCart, Heart, Bell, Calendar, MessageSquare
} from 'lucide-react';
import { workflowAPI } from '../api';

// Workflow node types
const NODE_TYPES = {
  trigger: {
    label: 'Triggers',
    color: 'bg-green-500',
    items: [
      { id: 'contact_created', name: 'Contact Created', icon: UserPlus, description: 'When a new contact is added' },
      { id: 'form_submitted', name: 'Form Submitted', icon: Target, description: 'When a landing page form is submitted' },
      { id: 'tag_added', name: 'Tag Added', icon: Filter, description: 'When a specific tag is added to contact' },
      { id: 'date_trigger', name: 'Date/Time', icon: Calendar, description: 'Trigger on specific date or recurring schedule' },
      { id: 'deal_stage_changed', name: 'Deal Stage Changed', icon: GitBranch, description: 'When deal moves to specific stage' },
    ]
  },
  action: {
    label: 'Actions',
    color: 'bg-blue-500',
    items: [
      { id: 'send_email', name: 'Send Email', icon: Mail, description: 'Send an email to the contact' },
      { id: 'add_tag', name: 'Add Tag', icon: Filter, description: 'Add a tag to the contact' },
      { id: 'remove_tag', name: 'Remove Tag', icon: Filter, description: 'Remove a tag from the contact' },
      { id: 'update_field', name: 'Update Field', icon: Edit2, description: 'Update a contact field' },
      { id: 'create_task', name: 'Create Task', icon: CheckCircle2, description: 'Create a task for team member' },
      { id: 'send_notification', name: 'Send Notification', icon: Bell, description: 'Send internal notification' },
      { id: 'webhook', name: 'Webhook', icon: Zap, description: 'Send data to external URL' },
    ]
  },
  condition: {
    label: 'Conditions',
    color: 'bg-amber-500',
    items: [
      { id: 'if_else', name: 'If/Else', icon: GitBranch, description: 'Branch based on condition' },
      { id: 'has_tag', name: 'Has Tag', icon: Filter, description: 'Check if contact has tag' },
      { id: 'field_value', name: 'Field Value', icon: Target, description: 'Check contact field value' },
    ]
  },
  delay: {
    label: 'Timing',
    color: 'bg-purple-500',
    items: [
      { id: 'wait', name: 'Wait', icon: Clock, description: 'Wait for specified time' },
      { id: 'wait_until', name: 'Wait Until', icon: Calendar, description: 'Wait until specific date/time' },
    ]
  }
};

// Pre-built templates
const TEMPLATES = [
  {
    id: 'welcome_series',
    name: 'Welcome Series',
    description: 'Automated welcome emails for new contacts',
    icon: UserPlus,
    color: 'bg-green-500',
    nodes: [
      { id: '1', type: 'trigger', nodeType: 'contact_created', position: { x: 100, y: 50 } },
      { id: '2', type: 'action', nodeType: 'send_email', config: { subject: 'Welcome!', template: 'welcome_1' }, position: { x: 100, y: 150 } },
      { id: '3', type: 'delay', nodeType: 'wait', config: { duration: 2, unit: 'days' }, position: { x: 100, y: 250 } },
      { id: '4', type: 'action', nodeType: 'send_email', config: { subject: 'Getting Started', template: 'welcome_2' }, position: { x: 100, y: 350 } },
      { id: '5', type: 'delay', nodeType: 'wait', config: { duration: 3, unit: 'days' }, position: { x: 100, y: 450 } },
      { id: '6', type: 'action', nodeType: 'send_email', config: { subject: 'Tips & Best Practices', template: 'welcome_3' }, position: { x: 100, y: 550 } },
    ],
    connections: [
      { from: '1', to: '2' },
      { from: '2', to: '3' },
      { from: '3', to: '4' },
      { from: '4', to: '5' },
      { from: '5', to: '6' },
    ]
  },
  {
    id: 'lead_nurturing',
    name: 'Lead Nurturing',
    description: 'Move leads through the sales funnel',
    icon: Target,
    color: 'bg-blue-500',
    nodes: [
      { id: '1', type: 'trigger', nodeType: 'tag_added', config: { tag: 'interested' }, position: { x: 100, y: 50 } },
      { id: '2', type: 'action', nodeType: 'send_email', config: { subject: 'Learn More', template: 'nurture_1' }, position: { x: 100, y: 150 } },
      { id: '3', type: 'delay', nodeType: 'wait', config: { duration: 3, unit: 'days' }, position: { x: 100, y: 250 } },
      { id: '4', type: 'condition', nodeType: 'has_tag', config: { tag: 'engaged' }, position: { x: 100, y: 350 } },
      { id: '5', type: 'action', nodeType: 'send_email', config: { subject: 'Case Study', template: 'nurture_2' }, position: { x: 250, y: 450 } },
      { id: '6', type: 'action', nodeType: 'create_task', config: { title: 'Follow up call' }, position: { x: -50, y: 450 } },
    ],
    connections: [
      { from: '1', to: '2' },
      { from: '2', to: '3' },
      { from: '3', to: '4' },
      { from: '4', to: '5', label: 'Yes' },
      { from: '4', to: '6', label: 'No' },
    ]
  },
  {
    id: 're_engagement',
    name: 'Re-engagement',
    description: 'Win back inactive contacts',
    icon: Heart,
    color: 'bg-red-500',
    nodes: [
      { id: '1', type: 'trigger', nodeType: 'date_trigger', config: { type: 'inactivity', days: 30 }, position: { x: 100, y: 50 } },
      { id: '2', type: 'action', nodeType: 'send_email', config: { subject: 'We miss you!', template: 'reengagement_1' }, position: { x: 100, y: 150 } },
      { id: '3', type: 'delay', nodeType: 'wait', config: { duration: 5, unit: 'days' }, position: { x: 100, y: 250 } },
      { id: '4', type: 'condition', nodeType: 'field_value', config: { field: 'last_email_opened', operator: 'is_set' }, position: { x: 100, y: 350 } },
      { id: '5', type: 'action', nodeType: 'add_tag', config: { tag: 're-engaged' }, position: { x: 250, y: 450 } },
      { id: '6', type: 'action', nodeType: 'add_tag', config: { tag: 'inactive' }, position: { x: -50, y: 450 } },
    ],
    connections: [
      { from: '1', to: '2' },
      { from: '2', to: '3' },
      { from: '3', to: '4' },
      { from: '4', to: '5', label: 'Opened' },
      { from: '4', to: '6', label: 'No Open' },
    ]
  },
  {
    id: 'post_purchase',
    name: 'Post-Purchase Follow-up',
    description: 'Engage customers after purchase',
    icon: ShoppingCart,
    color: 'bg-purple-500',
    nodes: [
      { id: '1', type: 'trigger', nodeType: 'deal_stage_changed', config: { stage: 'won' }, position: { x: 100, y: 50 } },
      { id: '2', type: 'action', nodeType: 'send_email', config: { subject: 'Thank you for your purchase!', template: 'post_purchase_1' }, position: { x: 100, y: 150 } },
      { id: '3', type: 'delay', nodeType: 'wait', config: { duration: 7, unit: 'days' }, position: { x: 100, y: 250 } },
      { id: '4', type: 'action', nodeType: 'send_email', config: { subject: 'How are you enjoying...?', template: 'feedback_request' }, position: { x: 100, y: 350 } },
      { id: '5', type: 'delay', nodeType: 'wait', config: { duration: 14, unit: 'days' }, position: { x: 100, y: 450 } },
      { id: '6', type: 'action', nodeType: 'add_tag', config: { tag: 'upsell_candidate' }, position: { x: 100, y: 550 } },
    ],
    connections: [
      { from: '1', to: '2' },
      { from: '2', to: '3' },
      { from: '3', to: '4' },
      { from: '4', to: '5' },
      { from: '5', to: '6' },
    ]
  },
];

// Visual Workflow Canvas Component
const WorkflowCanvas = ({ workflow, onUpdate, readOnly = false }) => {
  const canvasRef = useRef(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [editingNode, setEditingNode] = useState(null);  // Bug #11: Node configuration state
  const [nodeConfig, setNodeConfig] = useState({});

  const nodes = workflow?.nodes || [];
  const connections = workflow?.connections || [];

  const getNodeInfo = (node) => {
    const category = NODE_TYPES[node.type];
    const nodeType = category?.items.find(i => i.id === node.nodeType);
    return { category, nodeType };
  };

  const handleNodeClick = (node, e) => {
    e.stopPropagation();
    setSelectedNode(node.id === selectedNode ? null : node.id);
  };
  
  // Bug #11: Open node configuration dialog
  const handleNodeDoubleClick = (node, e) => {
    e.stopPropagation();
    setEditingNode(node);
    setNodeConfig(node.config || {});
  };
  
  // Bug #11: Save node configuration
  const handleSaveNodeConfig = () => {
    if (!editingNode || !onUpdate) return;
    
    const updatedNodes = nodes.map(n => 
      n.id === editingNode.id 
        ? { ...n, config: nodeConfig }
        : n
    );
    
    onUpdate({
      ...workflow,
      nodes: updatedNodes
    });
    
    setEditingNode(null);
    setNodeConfig({});
  };

  return (
    <>
    <div className="relative bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 min-h-[500px] overflow-auto">
      {/* Canvas */}
      <svg 
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ minHeight: '500px', minWidth: '100%' }}
      >
        {/* Connection lines */}
        {connections.map((conn, idx) => {
          const fromNode = nodes.find(n => n.id === conn.from);
          const toNode = nodes.find(n => n.id === conn.to);
          if (!fromNode || !toNode) return null;

          const fromX = (fromNode.position?.x || 100) + 100;
          const fromY = (fromNode.position?.y || 0) + 60;
          const toX = (toNode.position?.x || 100) + 100;
          const toY = toNode.position?.y || 0;

          return (
            <g key={idx}>
              <line
                x1={fromX}
                y1={fromY}
                x2={toX}
                y2={toY}
                stroke="#94a3b8"
                strokeWidth="2"
                markerEnd="url(#arrowhead)"
              />
              {conn.label && (
                <text
                  x={(fromX + toX) / 2}
                  y={(fromY + toY) / 2 - 5}
                  textAnchor="middle"
                  className="text-xs fill-slate-500"
                >
                  {conn.label}
                </text>
              )}
            </g>
          );
        })}
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" />
          </marker>
        </defs>
      </svg>

      {/* Nodes */}
      {nodes.map((node) => {
        const { category, nodeType } = getNodeInfo(node);
        const Icon = nodeType?.icon || Zap;
        const isSelected = selectedNode === node.id;

        return (
          <div
            key={node.id}
            className={`absolute w-[200px] bg-white rounded-lg shadow-md border-2 transition-all cursor-pointer ${
              isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-transparent hover:border-slate-300'
            }`}
            style={{
              left: node.position?.x || 100,
              top: node.position?.y || 0,
            }}
            onClick={(e) => handleNodeClick(node, e)}
            onDoubleClick={(e) => handleNodeDoubleClick(node, e)}
            title="Double-click to configure"
          >
            <div className={`${category?.color || 'bg-slate-500'} text-white px-3 py-2 rounded-t-lg flex items-center gap-2`}>
              <Icon className="h-4 w-4" />
              <span className="text-sm font-medium truncate">{nodeType?.name || node.nodeType}</span>
            </div>
            <div className="p-3">
              <p className="text-xs text-muted-foreground">
                {node.config?.subject || node.config?.tag || node.config?.title || nodeType?.description || 'Double-click to configure'}
              </p>
              {node.config?.duration && (
                <Badge variant="outline" className="mt-2 text-xs">
                  Wait {node.config.duration} {node.config.unit}
                </Badge>
              )}
            </div>
          </div>
        );
      })}

      {/* Empty state */}
      {nodes.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <Workflow className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Drag nodes here to build your workflow</p>
            <p className="text-sm text-muted-foreground">or select a template to get started</p>
          </div>
        </div>
      )}
    </div>
    
    {/* Bug #11: Node Configuration Dialog */}
    {editingNode && (
      <Dialog open={!!editingNode} onOpenChange={() => setEditingNode(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configure {getNodeInfo(editingNode).nodeType?.name || 'Node'}
            </DialogTitle>
            <DialogDescription>
              Set up the parameters for this workflow step
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Email Configuration */}
            {editingNode.nodeType === 'send_email' && (
              <>
                <div className="space-y-2">
                  <Label>Email Subject</Label>
                  <Input
                    value={nodeConfig.subject || ''}
                    onChange={(e) => setNodeConfig({...nodeConfig, subject: e.target.value})}
                    placeholder="Enter email subject..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Template</Label>
                  <Select 
                    value={nodeConfig.template || ''} 
                    onValueChange={(v) => setNodeConfig({...nodeConfig, template: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select template..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="welcome_1">Welcome Email #1</SelectItem>
                      <SelectItem value="welcome_2">Welcome Email #2</SelectItem>
                      <SelectItem value="nurture_1">Nurture Email #1</SelectItem>
                      <SelectItem value="promo_1">Promotional Email #1</SelectItem>
                      <SelectItem value="followup_1">Follow-up Email #1</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            
            {/* Tag Configuration */}
            {(editingNode.nodeType === 'add_tag' || editingNode.nodeType === 'remove_tag' || editingNode.nodeType === 'has_tag' || editingNode.nodeType === 'tag_added') && (
              <div className="space-y-2">
                <Label>Tag Name</Label>
                <Input
                  value={nodeConfig.tag || ''}
                  onChange={(e) => setNodeConfig({...nodeConfig, tag: e.target.value})}
                  placeholder="Enter tag name..."
                />
              </div>
            )}
            
            {/* Wait/Delay Configuration */}
            {(editingNode.nodeType === 'wait' || editingNode.nodeType === 'wait_until') && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Duration</Label>
                  <Input
                    type="number"
                    value={nodeConfig.duration || 1}
                    onChange={(e) => setNodeConfig({...nodeConfig, duration: parseInt(e.target.value)})}
                    min={1}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Select 
                    value={nodeConfig.unit || 'days'} 
                    onValueChange={(v) => setNodeConfig({...nodeConfig, unit: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minutes">Minutes</SelectItem>
                      <SelectItem value="hours">Hours</SelectItem>
                      <SelectItem value="days">Days</SelectItem>
                      <SelectItem value="weeks">Weeks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            {/* Task Creation Configuration */}
            {editingNode.nodeType === 'create_task' && (
              <>
                <div className="space-y-2">
                  <Label>Task Title</Label>
                  <Input
                    value={nodeConfig.title || ''}
                    onChange={(e) => setNodeConfig({...nodeConfig, title: e.target.value})}
                    placeholder="Enter task title..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={nodeConfig.description || ''}
                    onChange={(e) => setNodeConfig({...nodeConfig, description: e.target.value})}
                    placeholder="Task description..."
                    rows={2}
                  />
                </div>
              </>
            )}
            
            {/* Update Field Configuration */}
            {editingNode.nodeType === 'update_field' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Field Name</Label>
                  <Select 
                    value={nodeConfig.field || ''} 
                    onValueChange={(v) => setNodeConfig({...nodeConfig, field: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select field..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contact_type">Contact Type</SelectItem>
                      <SelectItem value="lead_score">Lead Score</SelectItem>
                      <SelectItem value="lifecycle_stage">Lifecycle Stage</SelectItem>
                      <SelectItem value="status">Status</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>New Value</Label>
                  <Input
                    value={nodeConfig.value || ''}
                    onChange={(e) => setNodeConfig({...nodeConfig, value: e.target.value})}
                    placeholder="Enter value..."
                  />
                </div>
              </div>
            )}
            
            {/* Condition Configuration */}
            {(editingNode.nodeType === 'if_else' || editingNode.nodeType === 'field_value') && (
              <>
                <div className="space-y-2">
                  <Label>Field to Check</Label>
                  <Select 
                    value={nodeConfig.field || ''} 
                    onValueChange={(v) => setNodeConfig({...nodeConfig, field: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select field..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email_opened">Email Opened</SelectItem>
                      <SelectItem value="link_clicked">Link Clicked</SelectItem>
                      <SelectItem value="lead_score">Lead Score</SelectItem>
                      <SelectItem value="contact_type">Contact Type</SelectItem>
                      <SelectItem value="last_activity">Last Activity</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Operator</Label>
                    <Select 
                      value={nodeConfig.operator || 'equals'} 
                      onValueChange={(v) => setNodeConfig({...nodeConfig, operator: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="equals">Equals</SelectItem>
                        <SelectItem value="not_equals">Not Equals</SelectItem>
                        <SelectItem value="contains">Contains</SelectItem>
                        <SelectItem value="greater_than">Greater Than</SelectItem>
                        <SelectItem value="less_than">Less Than</SelectItem>
                        <SelectItem value="is_set">Is Set</SelectItem>
                        <SelectItem value="is_not_set">Is Not Set</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Value</Label>
                    <Input
                      value={nodeConfig.value || ''}
                      onChange={(e) => setNodeConfig({...nodeConfig, value: e.target.value})}
                      placeholder="Enter value..."
                    />
                  </div>
                </div>
              </>
            )}
            
            {/* Notification Configuration */}
            {editingNode.nodeType === 'send_notification' && (
              <>
                <div className="space-y-2">
                  <Label>Notification Title</Label>
                  <Input
                    value={nodeConfig.title || ''}
                    onChange={(e) => setNodeConfig({...nodeConfig, title: e.target.value})}
                    placeholder="Notification title..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea
                    value={nodeConfig.message || ''}
                    onChange={(e) => setNodeConfig({...nodeConfig, message: e.target.value})}
                    placeholder="Notification message..."
                    rows={2}
                  />
                </div>
              </>
            )}
            
            {/* Webhook Configuration */}
            {editingNode.nodeType === 'webhook' && (
              <>
                <div className="space-y-2">
                  <Label>Webhook URL</Label>
                  <Input
                    value={nodeConfig.url || ''}
                    onChange={(e) => setNodeConfig({...nodeConfig, url: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>HTTP Method</Label>
                  <Select 
                    value={nodeConfig.method || 'POST'} 
                    onValueChange={(v) => setNodeConfig({...nodeConfig, method: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="POST">POST</SelectItem>
                      <SelectItem value="GET">GET</SelectItem>
                      <SelectItem value="PUT">PUT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingNode(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveNodeConfig}>
              <Save className="h-4 w-4 mr-2" />
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
};

export const MarketingAutomationPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [activeTab, setActiveTab] = useState('workflows');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newWorkflow, setNewWorkflow] = useState({ name: '', description: '' });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [stats, setStats] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [showExecuteDialog, setShowExecuteDialog] = useState(false);
  const [executeWorkflowId, setExecuteWorkflowId] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [executing, setExecuting] = useState(false);

  useEffect(() => {
    fetchWorkflows();
    fetchStats();
  }, []);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const res = await workflowAPI.getAll({ limit: 100 });
      setWorkflows(res.data?.workflows || res.data || []);
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
      setWorkflows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const [statsRes, executionsRes] = await Promise.all([
        workflowAPI.getStats(),
        workflowAPI.getExecutions({ limit: 20 })
      ]);
      setStats(statsRes.data);
      setExecutions(executionsRes.data?.executions || []);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts?limit=50`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setContacts(data || []);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  };

  const handleOpenExecute = (workflowId) => {
    setExecuteWorkflowId(workflowId);
    fetchContacts();
    setShowExecuteDialog(true);
  };

  const handleExecuteWorkflow = async () => {
    if (!selectedContactId) {
      toast.error('Please select a contact');
      return;
    }
    
    try {
      setExecuting(true);
      const res = await workflowAPI.execute({
        workflow_id: executeWorkflowId,
        contact_id: selectedContactId,
        trigger_type: 'manual'
      });
      
      if (res.data?.status === 'completed') {
        toast.success('Workflow executed successfully!');
      } else if (res.data?.status === 'waiting') {
        toast.info('Workflow started - waiting for timed delays');
      } else {
        toast.warning('Workflow execution: ' + res.data?.status);
      }
      
      setShowExecuteDialog(false);
      setSelectedContactId('');
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to execute workflow');
    } finally {
      setExecuting(false);
    }
  };

  const handleCreateWorkflow = async (template = null) => {
    try {
      const workflowData = {
        name: newWorkflow.name || (template ? `${template.name} Workflow` : 'New Workflow'),
        description: newWorkflow.description || (template?.description || ''),
        trigger_type: template?.nodes[0]?.nodeType || 'manual',
        nodes: template?.nodes || [],
        connections: template?.connections || [],
        status: 'draft'
      };

      const res = await workflowAPI.create(workflowData);
      toast.success('Workflow created');
      setShowCreateDialog(false);
      setNewWorkflow({ name: '', description: '' });
      setSelectedTemplate(null);
      fetchWorkflows();
      setSelectedWorkflow(res.data);
    } catch (error) {
      console.error('Create workflow error:', error);
      toast.error('Failed to create workflow');
    }
  };

  const handleUseTemplate = (template) => {
    setSelectedTemplate(template);
    setNewWorkflow({ 
      name: `${template.name} - ${new Date().toLocaleDateString()}`,
      description: template.description
    });
    setShowCreateDialog(true);
  };

  const handleToggleWorkflow = async (workflow) => {
    try {
      const newStatus = workflow.status === 'active' ? 'paused' : 'active';
      await workflowAPI.update(workflow.id, { status: newStatus });
      toast.success(`Workflow ${newStatus === 'active' ? 'activated' : 'paused'}`);
      fetchWorkflows();
    } catch (error) {
      toast.error('Failed to update workflow');
    }
  };

  const handleDeleteWorkflow = async (workflowId) => {
    if (!confirm('Delete this workflow?')) return;
    try {
      await workflowAPI.delete(workflowId);
      toast.success('Workflow deleted');
      if (selectedWorkflow?.id === workflowId) {
        setSelectedWorkflow(null);
      }
      fetchWorkflows();
    } catch (error) {
      toast.error('Failed to delete workflow');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="marketing-automation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Workflow className="h-6 w-6" />
            Marketing Automation
          </h1>
          <p className="text-muted-foreground">
            Create automated workflows to engage contacts and nurture leads
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { fetchWorkflows(); fetchStats(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-workflow-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Workflow
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Workflows</p>
                  <p className="text-2xl font-bold">{stats.workflows?.total || 0}</p>
                </div>
                <Workflow className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Workflows</p>
                  <p className="text-2xl font-bold">{stats.workflows?.active || 0}</p>
                </div>
                <Play className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Executions</p>
                  <p className="text-2xl font-bold">{stats.executions?.total || 0}</p>
                </div>
                <Zap className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Success Rate</p>
                  <p className="text-2xl font-bold">{stats.executions?.success_rate || 0}%</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="workflows">My Workflows</TabsTrigger>
          <TabsTrigger value="executions">Execution History</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          {selectedWorkflow && <TabsTrigger value="builder">Builder</TabsTrigger>}
        </TabsList>

        {/* Workflows List Tab */}
        <TabsContent value="workflows" className="space-y-4">
          {workflows.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Workflow className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No workflows yet</h3>
                <p className="text-muted-foreground mb-4 text-center max-w-md">
                  Create automated workflows to send emails, update contacts, and more based on triggers.
                </p>
                <div className="flex gap-2">
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Workflow
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('templates')}>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Use Template
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {workflows.map(workflow => (
                <Card key={workflow.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          workflow.status === 'active' ? 'bg-green-100' : 'bg-slate-100'
                        }`}>
                          <Workflow className={`h-6 w-6 ${
                            workflow.status === 'active' ? 'text-green-600' : 'text-slate-500'
                          }`} />
                        </div>
                        <div>
                          <h3 className="font-medium flex items-center gap-2">
                            {workflow.name}
                            <Badge variant={workflow.status === 'active' ? 'default' : 'secondary'}>
                              {workflow.status}
                            </Badge>
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {workflow.description || 'No description'}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Trigger: {workflow.trigger_type} • {workflow.nodes?.length || 0} nodes
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleOpenExecute(workflow.id)}
                          data-testid={`run-workflow-${workflow.id}`}
                        >
                          <Zap className="h-4 w-4 mr-2" />
                          Run
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleWorkflow(workflow)}
                        >
                          {workflow.status === 'active' ? (
                            <><Pause className="h-4 w-4 mr-2" /> Pause</>
                          ) : (
                            <><Play className="h-4 w-4 mr-2" /> Activate</>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedWorkflow(workflow);
                            setActiveTab('builder');
                          }}
                        >
                          <Edit2 className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteWorkflow(workflow.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Executions Tab */}
        <TabsContent value="executions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Execution History</CardTitle>
              <CardDescription>
                Recent workflow executions and their status
              </CardDescription>
            </CardHeader>
            <CardContent>
              {executions.length === 0 ? (
                <div className="text-center py-12">
                  <Zap className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-2">No executions yet</p>
                  <p className="text-sm text-muted-foreground">
                    Run a workflow to see execution history here
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {executions.map(exec => (
                    <div 
                      key={exec.id}
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        exec.status === 'completed' ? 'bg-green-50 border-green-200' :
                        exec.status === 'failed' ? 'bg-red-50 border-red-200' :
                        exec.status === 'waiting' ? 'bg-amber-50 border-amber-200' :
                        'bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          exec.status === 'completed' ? 'bg-green-100' :
                          exec.status === 'failed' ? 'bg-red-100' :
                          exec.status === 'waiting' ? 'bg-amber-100' :
                          'bg-slate-100'
                        }`}>
                          {exec.status === 'completed' ? <CheckCircle2 className="h-5 w-5 text-green-600" /> :
                           exec.status === 'failed' ? <XCircle className="h-5 w-5 text-red-600" /> :
                           exec.status === 'waiting' ? <Clock className="h-5 w-5 text-amber-600" /> :
                           <Zap className="h-5 w-5 text-slate-500" />}
                        </div>
                        <div>
                          <p className="font-medium">{exec.workflow_name}</p>
                          <p className="text-sm text-muted-foreground">
                            Contact: {exec.contact_email || exec.contact_id}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Trigger: {exec.trigger_type} • 
                            {exec.started_at && ` Started: ${new Date(exec.started_at).toLocaleString()}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={
                          exec.status === 'completed' ? 'default' :
                          exec.status === 'failed' ? 'destructive' :
                          exec.status === 'waiting' ? 'outline' :
                          'secondary'
                        }>
                          {exec.status}
                        </Badge>
                        {exec.execution_log?.length > 0 && (
                          <Badge variant="outline">
                            {exec.execution_log.length} steps
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {TEMPLATES.map(template => {
              const Icon = template.icon;
              return (
                <Card key={template.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className={`w-12 h-12 rounded-lg ${template.color} flex items-center justify-center`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <Button size="sm" onClick={() => handleUseTemplate(template)}>
                        Use Template
                      </Button>
                    </div>
                    <CardTitle className="text-lg mt-4">{template.name}</CardTitle>
                    <CardDescription>{template.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Badge variant="outline">{template.nodes.length} steps</Badge>
                      <span>•</span>
                      <span>
                        {template.nodes.filter(n => n.type === 'action').length} actions
                      </span>
                      <span>•</span>
                      <span>
                        {template.nodes.filter(n => n.type === 'delay').length} delays
                      </span>
                    </div>
                    
                    {/* Mini preview */}
                    <div className="mt-4 flex items-center gap-1 overflow-hidden">
                      {template.nodes.slice(0, 5).map((node, idx) => {
                        const category = NODE_TYPES[node.type];
                        const nodeType = category?.items.find(i => i.id === node.nodeType);
                        const NodeIcon = nodeType?.icon || Zap;
                        return (
                          <div key={idx} className="flex items-center">
                            <div className={`w-8 h-8 rounded ${category?.color} flex items-center justify-center`}>
                              <NodeIcon className="h-4 w-4 text-white" />
                            </div>
                            {idx < template.nodes.slice(0, 5).length - 1 && (
                              <ChevronRight className="h-4 w-4 text-muted-foreground mx-1" />
                            )}
                          </div>
                        );
                      })}
                      {template.nodes.length > 5 && (
                        <span className="text-xs text-muted-foreground ml-2">
                          +{template.nodes.length - 5} more
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Builder Tab */}
        {selectedWorkflow && (
          <TabsContent value="builder" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{selectedWorkflow.name}</CardTitle>
                  <CardDescription>{selectedWorkflow.description}</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={selectedWorkflow.status === 'active' ? 'default' : 'secondary'}>
                    {selectedWorkflow.status}
                  </Badge>
                  <Button variant="outline" size="sm">
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  {/* Node palette */}
                  <div className="col-span-1 space-y-4">
                    <h4 className="font-medium text-sm">Add Nodes</h4>
                    <ScrollArea className="h-[450px]">
                      {Object.entries(NODE_TYPES).map(([key, category]) => (
                        <div key={key} className="mb-4">
                          <p className="text-xs font-medium text-muted-foreground uppercase mb-2">
                            {category.label}
                          </p>
                          {category.items.map(item => {
                            const Icon = item.icon;
                            return (
                              <div
                                key={item.id}
                                className="flex items-center gap-2 p-2 rounded-lg border mb-2 cursor-grab hover:bg-slate-50"
                                draggable
                              >
                                <div className={`w-8 h-8 rounded ${category.color} flex items-center justify-center`}>
                                  <Icon className="h-4 w-4 text-white" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium">{item.name}</p>
                                  <p className="text-xs text-muted-foreground">{item.description}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </ScrollArea>
                  </div>
                  
                  {/* Canvas */}
                  <div className="col-span-3">
                    <WorkflowCanvas 
                      workflow={selectedWorkflow}
                      onUpdate={(updated) => setSelectedWorkflow(updated)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Create Workflow Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedTemplate ? `Create from ${selectedTemplate.name}` : 'Create New Workflow'}
            </DialogTitle>
            <DialogDescription>
              {selectedTemplate 
                ? 'Customize the template settings below'
                : 'Set up a new automation workflow'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Workflow Name</Label>
              <Input
                value={newWorkflow.name}
                onChange={(e) => setNewWorkflow({ ...newWorkflow, name: e.target.value })}
                placeholder="e.g., Welcome Email Series"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newWorkflow.description}
                onChange={(e) => setNewWorkflow({ ...newWorkflow, description: e.target.value })}
                placeholder="Describe what this workflow does..."
                rows={3}
              />
            </div>

            {selectedTemplate && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium mb-2">Template Preview</p>
                <div className="flex items-center gap-1">
                  {selectedTemplate.nodes.slice(0, 6).map((node, idx) => {
                    const category = NODE_TYPES[node.type];
                    const nodeType = category?.items.find(i => i.id === node.nodeType);
                    const Icon = nodeType?.icon || Zap;
                    return (
                      <div key={idx} className="flex items-center">
                        <div className={`w-6 h-6 rounded ${category?.color} flex items-center justify-center`}>
                          <Icon className="h-3 w-3 text-white" />
                        </div>
                        {idx < selectedTemplate.nodes.slice(0, 6).length - 1 && (
                          <ArrowRight className="h-3 w-3 text-muted-foreground mx-1" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateDialog(false);
              setSelectedTemplate(null);
              setNewWorkflow({ name: '', description: '' });
            }}>
              Cancel
            </Button>
            <Button onClick={() => handleCreateWorkflow(selectedTemplate)}>
              Create Workflow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Execute Workflow Dialog */}
      <Dialog open={showExecuteDialog} onOpenChange={setShowExecuteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Run Workflow
            </DialogTitle>
            <DialogDescription>
              Select a contact to run this workflow for
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Contact</Label>
              <Select value={selectedContactId} onValueChange={setSelectedContactId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a contact..." />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map(contact => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.first_name} {contact.last_name} - {contact.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The workflow will be executed for this contact
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowExecuteDialog(false);
              setSelectedContactId('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleExecuteWorkflow} 
              disabled={executing || !selectedContactId}
              data-testid="confirm-execute-btn"
            >
              {executing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Zap className="h-4 w-4 mr-2" />
              )}
              Execute Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketingAutomationPage;
